import { useMutation } from '@apollo/client';
import { useCallback } from 'react';

import {
  type SupplierCreateInput,
  type SupplierDetailsFragmentFragment,
} from '../../../generated';
import { type SupplierFormData } from '../components/SupplierForm/types';
import { SuppliersPageCreateSupplierMutation } from '../data/mutations/suppliersPageCreateSupplier.gql';
import { buildIntegrationPayload } from '../utils/buildIntegrationPayload';

type SuppliersPageCreateSupplierMutationData = {
  supplierCreate: SupplierDetailsFragmentFragment;
};

type SuppliersPageCreateSupplierMutationVariables = {
  supplier: SupplierCreateInput;
};

interface UseCreateSupplierResult {
  createSupplier: (
    data: SupplierFormData
  ) => Promise<SupplierDetailsFragmentFragment>;
  isLoading: boolean;
}

export const useCreateSupplier = (): UseCreateSupplierResult => {
  const [mutate, { loading }] = useMutation<
    SuppliersPageCreateSupplierMutationData,
    SuppliersPageCreateSupplierMutationVariables
  >(SuppliersPageCreateSupplierMutation);

  const createSupplier = useCallback(
    async (
      data: SupplierFormData
    ): Promise<SupplierDetailsFragmentFragment> => {
      const minOrderAmountCents =
        data.minimumOrderValue && data.minimumOrderValue !== ''
          ? Math.round(parseFloat(data.minimumOrderValue) * 100)
          : undefined;

      // Build integration array — required by SupplierCreateInput (non-optional),
      // so an empty array is sent when the form holds no notification config.
      // Shared with the edit path so the attachment checkboxes and the
      // comma-separated email lists behave the same in both.
      const integrationArray: SupplierCreateInput['integration'] =
        buildIntegrationPayload(data) ?? [];

      const input: SupplierCreateInput = {
        // Required fields
        id: crypto.randomUUID(),
        name: data.name,
        // What was chosen on the Settings tab. Hardcoding this meant the picker
        // looked like it worked and every distributor was created en-US. The
        // backend rejects a bare language such as `en`, and the form only offers
        // full tags, so the value is safe to pass through.
        locale: data.locale,
        integration: integrationArray,

        // Profile — send undefined (omit) for optional empty strings
        code: data.code.trim() || undefined,
        externalName: data.externalName.trim() || undefined,
        description: data.description?.trim() || undefined,
        primaryAssortment: data.primaryCategory || undefined,
        secondaryAssortments:
          data.secondaryCategories && data.secondaryCategories.length > 0
            ? data.secondaryCategories
            : undefined,
        deliveryCosts: data.deliveryFee?.trim() || undefined,
        ...(minOrderAmountCents !== undefined && {
          // P0-3: always include strict so the eCommerce flag is persisted
          minOrder: {
            price: {
              amount: minOrderAmountCents,
              currency: data.minimumOrderCurrency ?? 'EUR',
            },
            strict: data.minOrderStrict,
          },
        }),

        // Order preferences
        allowCustomProducts: data.allowCustomProducts,
        customerNumberMandatory: data.mandatoryCustomerNumber,
        deliveryDateMandatory: data.mandatoryDeliveryDate,
        allowBuyerComment: data.enableProductComments,
        enableFractionalUnits: data.allowFractionalUnits,
        allowOnlyOneActiveVariant: data.allowOnlyOneVariant,
        sortCartBy:
          data.sortBy === 'alphabetical' ? 'alphabetical' : 'orderGuide',

        // Catalog — P0-2: convert NO_CATALOG sentinel to backend pair
        ...(data.supplierDefaultSubCatalog === 'NO_CATALOG'
          ? { defaultSubCatalog: '', noDefaultCatalog: true }
          : {
              defaultSubCatalog: data.supplierDefaultSubCatalog || undefined,
              noDefaultCatalog: false,
            }),
        priceSetting: {
          displayInMarketplace: data.displayInMarketplace,
          displayToCustomers: data.displayToCustomers,
        },
        hideMyPrice: data.supplierHideMyPrice,

        // Settings
        isIntegrated: data.isIntegrated,
        // Sent on create even when false. The backend persists what it is
        // given, so leaving them out means a distributor created with either
        // switch on comes out with it off (PRO-279).
        enablePromotions: data.enablePromotions,
        enableTaxes: data.enableTaxes,
        pricingDateSource: data.pricingDateSource,
        calculationPrecision: data.calculationPrecision,

        // Time zone — omitted while unset; the backend rejects an empty string.
        ...(data.timezone && { timezone: data.timezone }),
      };

      const result = await mutate({
        variables: { supplier: input },
        refetchQueries: ['SuppliersPageSearchSuppliers'],
        awaitRefetchQueries: true,
      });

      // `errorPolicy: 'all'` resolves a rejection, so the backend's own error is
      // rethrown: the caller tells a duplicate code apart by its message.
      const [firstError] = result.errors ?? [];
      if (!result.data?.supplierCreate) {
        throw firstError ?? new Error('Supplier creation returned no data');
      }

      return result.data.supplierCreate;
    },
    [mutate]
  );

  return { createSupplier, isLoading: loading };
};
